import React, { useState } from "react";
import { X, Copy } from "lucide-react";
import { WorkOrderApi } from "api/workorders/service";

const UpdateContractSidebar = ({ 
  isOpen, 
  onClose, 
  workOrderId, 
  currentContract, 
  onUpdateSuccess 
}) => {
  const [formData, setFormData] = useState({
    contract_amount: "",
    end_date: "",
    scope_of_work: "",
    payment_terms: "",
    terms_and_conditions: "",
    approval_and_acknowledgement: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCopyFromPrevious = (field) => {
    if (currentContract && currentContract[field]) {
      setFormData(prev => ({
        ...prev,
        [field]: currentContract[field]
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.contract_amount || Number(formData.contract_amount) <= 0) {
      setError("Please enter a valid contract amount");
      return false;
    }
    if (!formData.end_date) {
      setError("Please select an end date");
      return false;
    }
    
    const currentEndDate = currentContract?.end_date ? new Date(currentContract.end_date) : null;
    const newEndDate = new Date(formData.end_date);
    
    if (currentEndDate && newEndDate < currentEndDate) {
      setError("End date cannot be earlier than current contract end date");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        end_date: formData.end_date,
        contract_amount: Number(formData.contract_amount),
        scope_of_work: formData.scope_of_work || "",
        payment_terms: formData.payment_terms || "",
        terms_and_conditions: formData.terms_and_conditions || "",
        approval_and_acknowledgement: formData.approval_and_acknowledgement || ""
      };

      // Import WorkOrderApi at the top of your actual file
      // import { WorkOrderApi } from "../../../api/workorders/service";
      const response = await WorkOrderApi.updateContract(workOrderId, payload);
      
      if (onUpdateSuccess) {
        onUpdateSuccess(response);
      }
      
      // Reset form and close
      setFormData({
        contract_amount: "",
        end_date: "",
        scope_of_work: "",
        payment_terms: "",
        terms_and_conditions: "",
        approval_and_acknowledgement: ""
      });
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to update contract");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const minDate = currentContract?.end_date || new Date().toISOString().split("T")[0];

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-[#232323]">Update Contract</h2>
          <button
            onClick={onClose}
            className="text-[#737373] hover:text-[#232323] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-5">
            {/* Contract Amount */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] text-[#232323] font-medium">
                  Contract Amount <span className="text-red-500">*</span>
                </label>
                {currentContract?.contract_amount && (
                  <button
                    type="button"
                    onClick={() => handleCopyFromPrevious('contract_amount')}
                    className="text-xs text-[#3B0069] hover:text-[#2d0051] flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy from previous
                  </button>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]">₹</span>
                <input
                  type="number"
                  value={formData.contract_amount}
                  onChange={(e) => handleInputChange('contract_amount', e.target.value)}
                  className="bg-[#F3F4F6] h-[48px] pl-8 pr-3 rounded-lg w-full text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Extend By (End Date) */}
            <div>
              <label className="text-[15px] text-[#232323] font-medium block mb-2">
                Extend By <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                min={minDate}
                className="bg-[#F3F4F6] h-[48px] px-3 rounded-lg w-full text-sm text-[#232323] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
              />
              {currentContract?.end_date && (
                <p className="text-xs text-[#737373] mt-1">
                  Current end date: {currentContract.end_date}
                </p>
              )}
            </div>

            {/* Scope of Work */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] text-[#232323] font-medium">
                  Scope of Work
                </label>
                {currentContract?.scope_of_work && (
                  <button
                    type="button"
                    onClick={() => handleCopyFromPrevious('scope_of_work')}
                    className="text-xs text-[#3B0069] hover:text-[#2d0051] flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy from previous
                  </button>
                )}
              </div>
              <textarea
                value={formData.scope_of_work}
                onChange={(e) => handleInputChange('scope_of_work', e.target.value)}
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-sm text-[#232323] resize-none h-[100px] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                placeholder="Enter scope of work"
              />
            </div>

            {/* Payment Terms */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] text-[#232323] font-medium">
                  Payment Terms
                </label>
                {currentContract?.payment_terms && (
                  <button
                    type="button"
                    onClick={() => handleCopyFromPrevious('payment_terms')}
                    className="text-xs text-[#3B0069] hover:text-[#2d0051] flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy from previous
                  </button>
                )}
              </div>
              <textarea
                value={formData.payment_terms}
                onChange={(e) => handleInputChange('payment_terms', e.target.value)}
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-sm text-[#232323] resize-none h-[100px] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                placeholder="Enter payment terms"
              />
            </div>

            {/* Terms & Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] text-[#232323] font-medium">
                  Terms & Conditions
                </label>
                {currentContract?.terms_and_conditions && (
                  <button
                    type="button"
                    onClick={() => handleCopyFromPrevious('terms_and_conditions')}
                    className="text-xs text-[#3B0069] hover:text-[#2d0051] flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy from previous
                  </button>
                )}
              </div>
              <textarea
                value={formData.terms_and_conditions}
                onChange={(e) => handleInputChange('terms_and_conditions', e.target.value)}
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-sm text-[#232323] resize-none h-[100px] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                placeholder="Enter terms and conditions"
              />
            </div>

            {/* Approval & Acknowledgement */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[15px] text-[#232323] font-medium">
                  Approval & Acknowledgement
                </label>
                {currentContract?.approval_and_acknowledgement && (
                  <button
                    type="button"
                    onClick={() => handleCopyFromPrevious('approval_and_acknowledgement')}
                    className="text-xs text-[#3B0069] hover:text-[#2d0051] flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy from previous
                  </button>
                )}
              </div>
              <textarea
                value={formData.approval_and_acknowledgement}
                onChange={(e) => handleInputChange('approval_and_acknowledgement', e.target.value)}
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-sm text-[#232323] resize-none h-[100px] focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                placeholder="Enter approval and acknowledgement"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-white">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-[#E3E3E3] text-[#232323] rounded-lg hover:bg-[#F3F4F6] transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#3B0069] text-white rounded-lg hover:bg-[#2d0051] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Contract"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateContractSidebar;